import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight, ArrowLeft, Briefcase, Lightbulb, Rocket, Palette,
  RefreshCw, HelpCircle, TrendingDown, UserMinus,
  Eye, Shield, MapPin, DollarSign, Layers, Heart, Map, Users,
  Code, MessageSquare, Brain, Zap, Bot, Wrench, Wand2,
  Handshake, Target, BookOpen, Mic, Award, Star, Check, Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  {
    id: 'status', title: "Where Are You Right Now?",
    subtitle: "Select the option that best describes your current professional status",
    type: 'single', field: 'current_status',
    options: [
      { value: 'Employed', icon: Briefcase },
      { value: 'Underemployed', icon: TrendingDown },
      { value: 'Laid Off', icon: UserMinus },
      { value: 'Entrepreneur', icon: Rocket },
      { value: 'Aspiring Entrepreneur', icon: Lightbulb },
      { value: 'Creator', icon: Palette },
      { value: 'In Transition', icon: RefreshCw },
      { value: 'Overwhelmed & Unsure', icon: HelpCircle },
    ]
  },
  {
    id: 'challenge', title: "What's Your Primary Challenge?",
    subtitle: "The biggest obstacle standing between you and your next level",
    type: 'single', field: 'primary_challenge',
    options: [
      { value: 'Lack of Clarity', icon: Eye },
      { value: 'Fear of AI', icon: Bot },
      { value: 'Career Uncertainty', icon: MapPin },
      { value: 'Income Instability', icon: DollarSign },
      { value: 'Too Many Ideas', icon: Layers },
      { value: 'Low Confidence', icon: Heart },
      { value: 'Lack of Strategy', icon: Map },
      { value: 'Lack of Support', icon: Users },
      { value: 'Lack of Relevant Skills', icon: BookOpen },
      { value: 'Feeling Left Behind', icon: TrendingDown },
    ]
  },
  {
    id: 'skills', title: "What Are Your Core Strengths?",
    subtitle: "Select all skills you bring to the table",
    type: 'multi', field: 'skills',
    options: [
      { value: 'Communication', icon: MessageSquare },
      { value: 'Leadership', icon: Award },
      { value: 'Business', icon: Briefcase },
      { value: 'Technical', icon: Code },
      { value: 'Digital', icon: Zap },
      { value: 'Sales', icon: DollarSign },
      { value: 'Strategy', icon: Target },
      { value: 'Creative', icon: Palette },
    ]
  },
  {
    id: 'ai_tools', title: "Your AI Readiness",
    subtitle: "Which AI tools have you explored? Select all that apply",
    type: 'multi', field: 'ai_tools',
    options: [
      { value: 'ChatGPT', icon: Bot },
      { value: 'Claude', icon: Brain },
      { value: 'Gemini', icon: Star },
      { value: 'Perplexity', icon: Eye },
      { value: 'Midjourney', icon: Palette },
      { value: 'Canva AI', icon: Wand2 },
      { value: 'Notion AI', icon: BookOpen },
      { value: 'Copilot', icon: Code },
      { value: 'Automation Tools', icon: Wrench },
      { value: 'No AI Tools Yet', icon: HelpCircle },
    ]
  },
  {
    id: 'community', title: "What Do You Want From This Network?",
    subtitle: "Select everything that matters to you",
    type: 'multi', field: 'community_intent',
    options: [
      { value: 'Accountability', icon: Check },
      { value: 'Networking', icon: Users },
      { value: 'Clarity', icon: Eye },
      { value: 'AI Skill Building', icon: Brain },
      { value: 'Business Growth', icon: TrendingDown },
      { value: 'Career Pivot Support', icon: RefreshCw },
      { value: 'Collaboration', icon: Handshake },
      { value: 'Encouragement', icon: Heart },
      { value: 'Live Sessions', icon: Mic },
      { value: 'Thought Partnership', icon: Lightbulb },
    ]
  },
  {
    id: 'vision', title: "Define Your Vision",
    subtitle: "What does success look like for you at each milestone?",
    type: 'text', field: 'vision',
    fields: [
      { key: 'goals_30', label: '30-Day Power Goal', placeholder: 'In the next 30 days, I will...' },
      { key: 'goals_90', label: '90-Day Milestone', placeholder: 'In 90 days, I want to have...' },
      { key: 'goals_180', label: '180-Day Transformation', placeholder: 'In 6 months, my life will look like...' },
    ]
  },
  {
    id: 'blockers', title: "What's Holding You Back?",
    subtitle: "Be honest. Naming it is the first step to moving past it",
    type: 'multi', field: 'blockers',
    options: [
      { value: 'Fear', icon: Shield },
      { value: 'Confusion', icon: HelpCircle },
      { value: 'Lack of Money', icon: DollarSign },
      { value: 'Lack of Time', icon: Target },
      { value: 'Lack of Support', icon: Users },
      { value: 'Low Confidence', icon: Heart },
      { value: 'Procrastination', icon: RefreshCw },
      { value: 'Lack of Structure', icon: Map },
      { value: 'Skill Gaps', icon: BookOpen },
      { value: 'Overwhelm', icon: Layers },
    ]
  },
];

const STEP_LABELS = ['Status', 'Challenge', 'Skills', 'AI Tools', 'Network', 'Vision', 'Blockers'];

export default function Onboarding() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [archetype, setArchetype] = useState(null);
  const [fadeIn, setFadeIn] = useState(true);

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  const [formData, setFormData] = useState({
    current_status: '', primary_challenge: '',
    skills: [], ai_tools: [], community_intent: [],
    goals_30: '', goals_90: '', goals_180: '', blockers: []
  });

  const animateStep = (newStep) => {
    setFadeIn(false);
    setTimeout(() => { setStep(newStep); setFadeIn(true); }, 200);
  };

  const handleSingleSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: prev[field] === value ? '' : value }));
  };

  const handleMultiToggle = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const canProceed = () => {
    const s = STEPS[step];
    if (!s) return false;
    if (s.type === 'single') return formData[s.field] !== '';
    if (s.type === 'multi') return formData[s.field].length > 0;
    if (s.type === 'text') return true; // Vision step is always skippable
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/onboarding/submit`, {
        user_id: user.user_id, email: user.email, ...formData
      });
      setArchetype(response.data.archetype);
      animateStep(STEPS.length);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const isReveal = step === STEPS.length && archetype;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Purple accent header */}
      <div className="bg-gradient-to-r from-purple-700 via-purple-600 to-pink-600 py-6 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-center space-x-3">
          <img src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png" alt="Pivot To Power" className="w-10 h-10 rounded-full border-2 border-white/30" />
          <span className="text-2xl font-cormorant font-bold text-white">Power Path Finder</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">

        {/* Journey Map Progress */}
        {!isReveal && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-3">
              {STEP_LABELS.map((label, i) => (
                <div key={i} className="flex flex-col items-center flex-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-inter font-bold transition-all duration-300 ${
                    i < step ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-md shadow-purple-200'
                    : i === step ? 'bg-white border-2 border-purple-500 text-purple-600 shadow-md'
                    : 'bg-gray-100 border border-gray-200 text-gray-400'
                  }`} data-testid={`step-indicator-${i}`}>
                    {i < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-[10px] mt-1.5 font-inter hidden sm:block ${
                    i <= step ? 'text-gray-700 font-medium' : 'text-gray-400'
                  }`}>{label}</span>
                </div>
              ))}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-gradient-to-r from-purple-600 to-pink-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className={`transition-all duration-200 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          {!isReveal && step < STEPS.length && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8" data-testid={`onboarding-step-${STEPS[step].id}`}>
              <h2 className="text-2xl sm:text-3xl font-cormorant font-bold text-gray-900 mb-2">
                {STEPS[step].title}
              </h2>
              <p className="text-gray-500 font-inter text-sm mb-8">{STEPS[step].subtitle}</p>

              {/* Single Select */}
              {STEPS[step].type === 'single' && (
                <div className="grid grid-cols-2 gap-3">
                  {STEPS[step].options.map(opt => {
                    const Icon = opt.icon;
                    const selected = formData[STEPS[step].field] === opt.value;
                    return (
                      <button
                        key={opt.value}
                        onClick={() => handleSingleSelect(STEPS[step].field, opt.value)}
                        className={`p-4 rounded-xl border transition-all duration-200 text-left flex items-center space-x-3 group ${
                          selected
                            ? 'bg-purple-50 border-purple-400 shadow-sm'
                            : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50/30'
                        }`}
                        data-testid={`option-${opt.value.toLowerCase().replace(/[\s&]/g, '-')}`}
                      >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                          selected ? 'bg-purple-100' : 'bg-gray-50 group-hover:bg-purple-50'
                        }`}>
                          <Icon className={`w-5 h-5 ${selected ? 'text-purple-600' : 'text-gray-400 group-hover:text-purple-500'}`} />
                        </div>
                        <span className={`font-inter text-sm ${selected ? 'text-purple-900 font-medium' : 'text-gray-700'}`}>
                          {opt.value}
                        </span>
                        {selected && <Check className="w-4 h-4 text-purple-600 ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Multi Select */}
              {STEPS[step].type === 'multi' && (
                <>
                  <div className="text-xs text-purple-600 font-inter font-medium mb-4">Select all that apply</div>
                  <div className="grid grid-cols-2 gap-3">
                    {STEPS[step].options.map(opt => {
                      const Icon = opt.icon;
                      const selected = formData[STEPS[step].field].includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          onClick={() => handleMultiToggle(STEPS[step].field, opt.value)}
                          className={`p-4 rounded-xl border transition-all duration-200 text-left flex items-center space-x-3 group ${
                            selected
                              ? 'bg-purple-50 border-purple-400 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-purple-300 hover:bg-purple-50/30'
                          }`}
                          data-testid={`option-${opt.value.toLowerCase().replace(/[\s&]/g, '-')}`}
                        >
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                            selected ? 'bg-purple-100' : 'bg-gray-50 group-hover:bg-purple-50'
                          }`}>
                            <Icon className={`w-5 h-5 ${selected ? 'text-purple-600' : 'text-gray-400 group-hover:text-purple-500'}`} />
                          </div>
                          <span className={`font-inter text-sm ${selected ? 'text-purple-900 font-medium' : 'text-gray-700'}`}>
                            {opt.value}
                          </span>
                          {selected && <Check className="w-4 h-4 text-purple-600 ml-auto" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Text Fields */}
              {STEPS[step].type === 'text' && (
                <div className="space-y-6">
                  {STEPS[step].fields.map(f => (
                    <div key={f.key}>
                      <label className="block text-purple-700 text-sm font-inter font-medium mb-2">{f.label}</label>
                      <textarea
                        value={formData[f.key]}
                        onChange={(e) => setFormData(prev => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 focus:border-purple-500 focus:ring-1 focus:ring-purple-200 outline-none transition-all placeholder:text-gray-400 min-h-[90px] font-inter text-sm resize-none"
                        placeholder={f.placeholder}
                        data-testid={`input-${f.key}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                <button
                  onClick={() => step > 0 && animateStep(step - 1)}
                  disabled={step === 0}
                  className={`flex items-center space-x-2 font-inter text-sm py-3 px-6 rounded-full transition-all ${
                    step === 0 ? 'opacity-30 cursor-not-allowed text-gray-400' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  data-testid="onboarding-back-button"
                >
                  <ArrowLeft className="w-4 h-4" /><span>Back</span>
                </button>

                {step < STEPS.length - 1 ? (
                  <button
                    onClick={() => canProceed() && animateStep(step + 1)}
                    disabled={!canProceed()}
                    className={`flex items-center space-x-2 font-inter font-medium text-sm py-3 px-8 rounded-full transition-all ${
                      canProceed()
                        ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    data-testid="onboarding-next-button"
                  >
                    <span>Continue</span><ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !canProceed()}
                    className="flex items-center space-x-2 font-inter font-medium text-sm py-3 px-8 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-200 transition-all disabled:opacity-50"
                    data-testid="onboarding-submit-button"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /><span>Analyzing...</span></>
                    ) : (
                      <><span>Reveal My Path</span><Star className="w-4 h-4" /></>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Archetype Reveal */}
          {isReveal && (
            <div className="text-center animate-fadeIn" data-testid="archetype-reveal">
              <div className="inline-block mb-6">
                <span className="text-xs font-inter font-semibold text-purple-600 uppercase tracking-[0.2em]">
                  Your Power Path Revealed
                </span>
              </div>

              <div className="bg-white rounded-2xl border border-purple-200 p-10 mb-8 shadow-lg shadow-purple-100">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-200">
                  <Award className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-4xl sm:text-5xl font-cormorant font-bold bg-gradient-to-r from-purple-700 via-pink-600 to-purple-700 bg-clip-text text-transparent mb-4">
                  {archetype.archetype}
                </h2>
                <div className="inline-flex items-center space-x-2 bg-purple-50 px-4 py-2 rounded-full mb-8">
                  <Zap className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700 font-inter text-sm">AI Readiness: <span className="text-purple-700 font-semibold">{archetype.ai_readiness}</span></span>
                </div>

                <div className="grid md:grid-cols-2 gap-6 text-left mb-8">
                  <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-100">
                    <h3 className="text-emerald-700 font-inter font-semibold text-sm uppercase tracking-wider mb-3">Your Strengths</h3>
                    <ul className="space-y-2">
                      {archetype.strengths.map((s, i) => (
                        <li key={i} className="flex items-center space-x-2 text-gray-700 font-inter text-sm">
                          <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /><span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-pink-50 rounded-xl p-6 border border-pink-100">
                    <h3 className="text-pink-700 font-inter font-semibold text-sm uppercase tracking-wider mb-3">Growth Edges</h3>
                    <ul className="space-y-2">
                      {archetype.weaknesses.map((w, i) => (
                        <li key={i} className="flex items-center space-x-2 text-gray-700 font-inter text-sm">
                          <Target className="w-4 h-4 text-pink-500 flex-shrink-0" /><span>{w}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100 mb-6">
                  <h3 className="text-gray-900 font-cormorant font-bold text-xl mb-2">Recommended Path</h3>
                  <p className="text-purple-700 font-inter font-medium">{archetype.recommended_path}</p>
                </div>
              </div>

              <button
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white font-inter font-bold px-12 py-4 rounded-full transition-all shadow-lg shadow-purple-200 text-lg"
                data-testid="enter-dashboard-button"
              >
                Enter Your Dashboard
                <ArrowRight className="w-5 h-5 inline ml-2" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
